import React from 'react';
import { motion } from 'framer-motion';
import { Star } from 'lucide-react';

const testimonials = [
  {
    name: 'Marcus Vance',
    role: 'Chief Executive Officer',
    company: 'Nexus Supply Chain',
    location: 'Austin, TX',
    flag: '🇺🇸',
    quote: 'Zynqora Edge replaced our fragmented logistics dispatch with a custom automated AI engine. We cut 35+ manual hours weekly and eliminated dispatch delays entirely within 60 days.',
    avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=200&q=80',
    metric: '40% Faster Ops',
  },
  {
    name: 'Elena Rostova',
    role: 'Managing Partner',
    company: 'Apex FinTech Capital',
    location: 'Toronto, ON',
    flag: '🇨🇦',
    quote: 'Their team built a 24/7 financial lead agent that pre-qualifies investors automatically. Our inbound conversion jumped by 150% in the first quarter of release.',
    avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=200&q=80',
    metric: '150% Conversion ↑',
  },
  {
    name: 'David Chen',
    role: 'Founder & CTO',
    company: 'HealthPulse Systems',
    location: 'Vancouver, BC',
    flag: '🇨🇦',
    quote: 'Working directly with M. Haris Kamran and the Zynqora engineering team gave us immediate confidence. They delivered our HIPAA-compliant portal on budget with zero delays.',
    avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=200&q=80',
    metric: '99.98% Uptime',
  },
  {
    name: 'Sarah Gallagher',
    role: 'VP of Operations',
    company: 'Horizon E-Commerce',
    location: 'Chicago, IL',
    flag: '🇺🇸',
    quote: 'The automated inventory reconciliation pipeline Zynqora built prevented inventory stockouts during peak quarter sales. Invaluable engineering team.',
    avatar: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=200&q=80',
    metric: '$1.2M Saved',
  },
  {
    name: 'Michael Sterling',
    role: 'Founder & CEO',
    company: 'CloudScale SaaS',
    location: 'New York, NY',
    flag: '🇺🇸',
    quote: 'Fixed-scope pricing with senior engineers who actually understand SaaS architecture. Zynqora Edge is our primary software engineering partner.',
    avatar: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&w=200&q=80',
    metric: '3.2x ROI',
  },
];

const Testimonials = () => {
  return (
    <section id="reviews" className="py-24 bg-[#F8FAFC] text-slate-900 relative overflow-hidden">
      {/* Subtle background texture */}
      <div className="absolute inset-0 opacity-30 pointer-events-none" style={{
        backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(2,132,199,0.06) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(147,51,234,0.04) 0%, transparent 50%)',
      }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="section-label mb-3 justify-center">Verified Executive Reviews</div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight mb-4">
            Trusted by business leaders across{' '}
            <span className="text-sky-600">the USA &amp; Canada.</span>
          </h2>
          <p className="text-slate-600 text-base">
            Read direct feedback from business owners, founders, and executives who rely on Zynqora Edge software engineering.
          </p>
        </div>

        {/* Testimonial Bento Grid */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {testimonials.map((t, idx) => (
            <motion.div
              key={t.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ delay: idx * 0.08, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="bento-card-light p-7 flex flex-col justify-between group"
            >
              <div>
                {/* Rating Stars & Location Badge */}
                <div className="flex items-center justify-between mb-4 gap-2">
                  <div className="flex items-center gap-0.5 text-amber-400">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={14} fill="currentColor" />
                    ))}
                  </div>
                  {/* Fixed: shortened location text to prevent overflow */}
                  <span className="text-[10px] font-mono font-semibold px-2 py-1 rounded-full bg-slate-100 border border-slate-200 text-slate-600 flex items-center gap-1 flex-shrink-0 whitespace-nowrap">
                    <span>{t.flag}</span>
                    <span>{t.location}</span>
                  </span>
                </div>

                {/* Quote Text */}
                <blockquote className="text-slate-700 text-sm leading-relaxed mb-6 font-normal">
                  &ldquo;{t.quote}&rdquo;
                </blockquote>
              </div>

              {/* Author Info & Metric */}
              <div className="pt-5 border-t border-slate-100 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <img
                    src={t.avatar}
                    alt={t.name}
                    className="w-10 h-10 rounded-full object-cover border border-slate-200 shadow-sm flex-shrink-0"
                    loading="lazy"
                  />
                  <div className="min-w-0">
                    <h4 className="font-heading font-bold text-sm text-slate-900 truncate">{t.name}</h4>
                    <p className="text-[11px] text-slate-500 truncate">{t.role}, <span className="text-sky-600 font-semibold">{t.company}</span></p>
                  </div>
                </div>

                <div className="flex-shrink-0">
                  <span className="text-[10px] font-mono font-bold px-2 py-1 rounded-md bg-sky-50 text-sky-700 border border-sky-100 whitespace-nowrap">
                    {t.metric}
                  </span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

      </div>
    </section>
  );
};

export default Testimonials;
